import React, { useState, useEffect } from "react";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";

// Component to display star rating
const StarRating = ({
  rating = 0,
  totalRatings = 0,
  size = 20,
  showCount = true,
}) => {
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon
          key={`full-${i}`}
          as={FaStar}
          color="orange.500"
          boxSize={`${size}px`}
        />
      );
    }

    // Half star
    if (hasHalfStar && fullStars < 5) {
      stars.push(
        <Icon
          key="half"
          as={FaStarHalfAlt}
          color="orange.500"
          boxSize={`${size}px`}
        />
      );
    }

    // Empty stars
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon
          key={`empty-${i}`}
          as={FaRegStar}
          color="gray.300"
          boxSize={`${size}px`}
        />
      );
    }

    return stars;
  };

  return (
    <HStack spacing={1} align="center">
      {renderStars()}
      {showCount && (
        <>
          <Text fontSize="sm" color="gray.600" ml={2}>
            {rating > 0 ? rating.toFixed(1) : "0.0"}
          </Text>
          {totalRatings > 0 && (
            <Text fontSize="sm" color="gray.500">
              ({totalRatings} {totalRatings === 1 ? "review" : "reviews"})
            </Text>
          )}
        </>
      )}
    </HStack>
  );
};

// Interactive rating component for users to rate
const InteractiveStarRating = ({
  onRatingChange,
  currentRating = 0,
  size = 32,
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(currentRating);

  useEffect(() => {
    setSelectedRating(currentRating);
  }, [currentRating]);

  const handleClick = (rating) => {
    setSelectedRating(rating);
    onRatingChange(rating);
  };

  const renderInteractiveStars = () => {
    const stars = [];
    const displayRating = hoverRating || selectedRating;

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon
          key={i}
          as={FaStar}
          color={i <= displayRating ? "orange.500" : "gray.300"}
          boxSize={`${size}px`}
          cursor="pointer"
          transition="all 0.2s"
          _hover={{
            transform: "scale(1.1)",
            color: "orange.400",
          }}
          onMouseEnter={() => setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => handleClick(i)}
        />
      );
    }

    return stars;
  };

  return (
    <Box>
      <HStack spacing={2}>{renderInteractiveStars()}</HStack>
      <Text fontSize="sm" color="gray.600" mt={2}>
        {hoverRating
          ? `Rate ${hoverRating} star${hoverRating > 1 ? "s" : ""}`
          : selectedRating
          ? `Your rating: ${selectedRating} star${
              selectedRating > 1 ? "s" : ""
            }`
          : "Click to rate"}
      </Text>
    </Box>
  );
};

export { StarRating, InteractiveStarRating };
